import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, clearUser, setLoading } from '../redux/slices/authSlice';
import { authService } from '../services/api';
import { setCookie, deleteCookie } from '../utils/cookies';
import { hydrateWishlistFromServer } from '../utils/wishlist';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
	const dispatch = useDispatch();
	const { user, isAuthenticated, loading } = useSelector((state) => state.auth);

	const clearAuth = useCallback(() => {
		// Clear localStorage
		localStorage.removeItem('user');
		// Clear cookies (tokens only)
		deleteCookie('token');
		deleteCookie('refreshToken');
		// Clear Redux state
		dispatch(clearUser());
	}, [dispatch]);

	const checkAuth = useCallback(async () => {
		dispatch(setLoading(true));

		// Lấy user data từ localStorage để hiển thị ngay (tránh flash)
		const storedUser = localStorage.getItem('user');
		if (storedUser) {
			try {
				const userData = JSON.parse(storedUser);
				dispatch(setUser(userData));
			} catch (error) {
				console.error('Parse user data error:', error);
				clearAuth();
				dispatch(setLoading(false));
				return;
			}

			// Sau đó gọi whoAmI để lấy dữ liệu mới nhất từ server
			try {
				const response = await authService.whoAmI();
				if (response.success) {
					localStorage.setItem('user', JSON.stringify(response.data));
					dispatch(setUser(response.data));
				}
			} catch (error) {
				// Token hết hạn hoặc không hợp lệ → logout
				console.error('whoAmI error:', error);
				if (error?.response?.status === 401) {
					clearAuth();
				}
			}
		}

		dispatch(setLoading(false));
	}, [dispatch, clearAuth]);

	useEffect(() => {
		// Kiểm tra auth khi app load
		checkAuth();
	}, [checkAuth]);

	// Đồng bộ danh sách yêu thích từ server mỗi khi userId thay đổi (đăng nhập)
	const userId = user?.id || user?._id;
	useEffect(() => {
		if (userId) {
			hydrateWishlistFromServer(userId);
		}
	}, [userId]);

	const login = async (credentials) => {
		try {
			const response = await authService.login(credentials);
			if (response.success && response.data.user) {
				// Lưu tokens vào cookies
				setCookie('token', response.data.token, 7);
				setCookie('refreshToken', response.data.refreshToken, 7);
				// Lưu user data vào localStorage
				localStorage.setItem('user', JSON.stringify(response.data.user));
				dispatch(setUser(response.data.user));
			}
			return response;
		} catch (error) {
			throw error;
		}
	};

	const register = async (userData) => {
		try {
			const response = await authService.register(userData);
			if (response.success && response.data.user) {
				// Lưu tokens vào cookies
				setCookie('token', response.data.token, 7);
				setCookie('refreshToken', response.data.refreshToken, 7);
				// Lưu user data vào localStorage
				localStorage.setItem('user', JSON.stringify(response.data.user));
				dispatch(setUser(response.data.user));
			}
			return response;
		} catch (error) {
			throw error;
		}
	};

	const logout = async () => {
		try {
			// Call API to revoke current token on server
			await authService.revokeToken();
		} catch (error) {
			console.error('Revoke token error:', error);
			// If revoke fails, still try logout to revoke all tokens
			try {
				await authService.logout();
			} catch (logoutError) {
				console.error('Logout error:', logoutError);
			}
		} finally {
			// Always clear local auth state
			clearAuth();
			// Redirect to loading page (3s) then login
			window.location.href = '/loading?next=' + encodeURIComponent('/login');
		}
	};

	const refreshUserData = useCallback(async () => {
		try {
			const response = await authService.whoAmI();
			if (response.success) {
				dispatch(setUser(response.data));
				localStorage.setItem('user', JSON.stringify(response.data));
			}
		} catch (error) {
			console.error('Refresh user data error:', error);
		}
	}, [dispatch]);

	const value = {
		user,
		loading,
		login,
		register,
		logout,
		refreshUserData,
		isAuthenticated,
	};

	return <AuthContext.Provider value={ value }>{ children }</AuthContext.Provider>;
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};
